import React, { useEffect, useRef, useState } from "react";

const characterDesigns = [
  { shape: "flower", body: "#2452ee", face: "#8fa9f3" },
  { shape: "flower", body: "#ee3e79", face: "#f49ab8" },
  { shape: "drop", body: "#ff7417", face: "#ffb06f" },
  { shape: "drop", body: "#8f48df", face: "#c6a2ee" },
  { shape: "clover", body: "#91d626", face: "#c9f486" },
  { shape: "flower", body: "#20b7c6", face: "#8de2e7" },
  { shape: "flower", body: "#ffc21c", face: "#ffe38a" },
  { shape: "drop", body: "#0e61e9", face: "#89b5f3" },
  { shape: "clover", body: "#35bc78", face: "#9ae4c0" },
  { shape: "flower", body: "#f02f71", face: "#f69abb" },
];

// A dense, deliberately asymmetric masonry of pills packed around the hero
// content: bands above the heading (rows 1-2), staggered edge stacks hugging
// the copy (rows 3-6, left and right arranged differently), and bands below
// the CTA (rows 7-8). Gutters stay near 3% so there are no large empty
// pockets inside the shape. A vertical marked `follow: true`
// continues its horizontal partner's path, sharing the same color and edge
// coordinate for a seamless L-corner; for verticals, x marks the edge they
// sit flush against.
const laneBlueprints = [
  // The corners stay empty and each band's outer ends step inward as rows
  // move away from the vertical middle, so the whole composition reads as an
  // oval hugging the hero content rather than a rectangular frame.
  { direction: "horizontal", x: 25, y: 10, length: 28, count: 7, row: 1, origin: "left", color: "#fae9dc" },
  { direction: "horizontal", x: 56, y: 10, length: 18, count: 4, row: 1, origin: "right", color: "#dff2fc" },

  { direction: "horizontal", x: 9, y: 21, length: 14, count: 3, row: 2, origin: "left", color: "#dff7cf" },
  { direction: "vertical", x: 9, y: 21, length: 12, count: 2, row: 2, origin: "top", flush: "left", follow: true, color: "#dff7cf" },
  { direction: "horizontal", x: 26, y: 21, length: 20, count: 5, row: 2, origin: "left", color: "#efffc9" },
  { direction: "horizontal", x: 49, y: 21, length: 14, count: 3, row: 2, origin: "center", color: "#ffe1e7" },
  { direction: "horizontal", x: 66, y: 21, length: 20, count: 5, row: 2, origin: "right", color: "#edf8d7" },

  { direction: "horizontal", x: 11, y: 32, length: 12, count: 3, row: 3, origin: "left", color: "#fce1eb" },
  { direction: "vertical", x: 97, y: 32, length: 7, count: 1, row: 3, origin: "top", flush: "right", color: "#dff7cf" },
  { direction: "horizontal", x: 76, y: 35, length: 14, count: 3, row: 3, origin: "right", color: "#dcf8ea" },

  { direction: "horizontal", x: 2, y: 43, length: 20, count: 5, row: 4, origin: "left", color: "#aeefff" },
  { direction: "horizontal", x: 79, y: 47, length: 18, count: 4, row: 4, origin: "right", color: "#ffe1e7" },
  { direction: "vertical", x: 79, y: 47, length: 11, count: 2, row: 4, origin: "top", flush: "left", follow: true, color: "#ffe1e7" },

  { direction: "horizontal", x: 2, y: 54, length: 16, count: 4, row: 5, origin: "left", color: "#dff2fc" },
  { direction: "vertical", x: 18, y: 54, length: 11, count: 2, row: 5, origin: "top", flush: "right", follow: true, color: "#dff2fc" },
  { direction: "horizontal", x: 86, y: 59, length: 11, count: 2, row: 5, origin: "right", color: "#efffc9" },

  { direction: "horizontal", x: 4, y: 65, length: 9, count: 2, row: 6, origin: "left", color: "#dcf8ea" },
  { direction: "horizontal", x: 74, y: 69, length: 17, count: 4, row: 6, origin: "right", color: "#fae9dc" },

  { direction: "horizontal", x: 8, y: 79, length: 15, count: 3, row: 7, origin: "left", color: "#dff2fc" },
  { direction: "horizontal", x: 26, y: 79, length: 24, count: 6, row: 7, origin: "left", color: "#efffc9" },
  { direction: "horizontal", x: 53, y: 79, length: 18, count: 4, row: 7, origin: "center", color: "#fce1eb" },
  { direction: "horizontal", x: 74, y: 79, length: 16, count: 4, row: 7, origin: "right", color: "#dff7cf" },

  { direction: "horizontal", x: 32, y: 89, length: 26, count: 6, row: 8, origin: "center", color: "#ffe1e7" },
  { direction: "horizontal", x: 61, y: 89, length: 18, count: 4, row: 8, origin: "right", color: "#edf8d7" },
];

let characterOrder = 0;

const lanes = laneBlueprints.map((lane, laneIndex) => {
  const laneDelay = (lane.row - 1) * 70 + (lane.follow ? 260 : 0);
  const characters = Array.from({ length: lane.count }, (_, slot) => {
    const character = {
      ...characterDesigns[characterOrder % characterDesigns.length],
      order: characterOrder,
      progress: ((slot + 1) / (lane.count + 1)) * 100,
      rotate: (((laneIndex + slot) % 5) - 2) * 1.5,
      delay: laneDelay + 480 + slot * 55,
    };
    characterOrder += 1;
    return character;
  });

  return { ...lane, characters, delay: laneDelay };
});

function FlowerBody({ fill }) {
  return (
    <g fill={fill}>
      <ellipse cx="100" cy="106" rx="77" ry="73" />
      {Array.from({ length: 9 }, (_, index) => {
        const angle = -Math.PI / 2 + (Math.PI * 2 * index) / 9;
        return (
          <ellipse
            // The lobes share one color, so they read as a single soft silhouette.
            key={index}
            cx={100 + Math.cos(angle) * 61}
            cy={104 + Math.sin(angle) * 56}
            rx="34"
            ry="34"
          />
        );
      })}
    </g>
  );
}

function CloverBody({ fill }) {
  return (
    <g fill={fill}>
      <rect x="41" y="42" width="118" height="126" rx="39" />
      <ellipse cx="65" cy="69" rx="46" ry="48" />
      <ellipse cx="135" cy="69" rx="46" ry="48" />
      <ellipse cx="65" cy="139" rx="46" ry="48" />
      <ellipse cx="135" cy="139" rx="46" ry="48" />
    </g>
  );
}

function Body({ shape, fill }) {
  if (shape === "flower") return <FlowerBody fill={fill} />;
  if (shape === "clover") return <CloverBody fill={fill} />;
  return (
    <path
      fill={fill}
      d="M100 9C127 9 179 65 188 111c10 52-28 78-87 80-59 0-98-26-88-80C22 65 73 9 100 9Z"
    />
  );
}

function Eye({ x, blink = false, gaze = { x: 0, y: 0 } }) {
  return (
    <g transform={`translate(${x} 116)`}>
      <g className={blink ? "hero-eye is-blinking" : "hero-eye"}>
        <circle r="18" fill="#fff" />
        <g
          className="hero-pupil"
          style={{ transform: `translate(${gaze.x}px, ${gaze.y}px)` }}
        >
          <circle r="10.5" fill="#060606" />
          <circle cx="-3.4" cy="-3.8" r="3.35" fill="#fff" />
        </g>
      </g>
    </g>
  );
}

export function MiniCharacter({ shape, body, face }) {
  return (
    <svg className="card-character" viewBox="0 0 200 205" aria-hidden="true">
      <Body shape={shape} fill={body} />
      <path
        fill={face}
        d="M39 113c1-34 25-52 61-52s60 18 61 52c1 29-15 41-61 41s-62-12-61-41Z"
      />
      <Eye x="73" />
      <Eye x="127" />
    </svg>
  );
}

function useLivelyEyes(seed) {
  const [gaze, setGaze] = useState({ x: 0, y: 0 });
  const [blink, setBlink] = useState(false);
  const gazeTimer = useRef();
  const blinkTimer = useRef();
  const blinkEndTimer = useRef();

  useEffect(() => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const moveEyes = () => {
      const angle = Math.random() * Math.PI * 2;
      const distance = Math.sqrt(Math.random());
      setGaze({
        x: Number((Math.cos(angle) * 5 * distance).toFixed(2)),
        y: Number((Math.sin(angle) * 5 * distance).toFixed(2)),
      });
      gazeTimer.current = window.setTimeout(moveEyes, 900 + Math.random() * 1900);
    };

    const closeEyes = () => {
      setBlink(true);
      blinkEndTimer.current = window.setTimeout(() => setBlink(false), 120);
      blinkTimer.current = window.setTimeout(closeEyes, 2200 + Math.random() * 4700);
    };

    gazeTimer.current = window.setTimeout(moveEyes, 350 + seed * 90);
    blinkTimer.current = window.setTimeout(closeEyes, 1500 + Math.random() * 4000);

    return () => {
      window.clearTimeout(gazeTimer.current);
      window.clearTimeout(blinkTimer.current);
      window.clearTimeout(blinkEndTimer.current);
    };
  }, [seed]);

  return { gaze, blink };
}

/* A mini character with the same living eyes as the hero stage, for perching
   on showcase cards. */
export function LiveMiniCharacter({ shape, body, face, order = 0 }) {
  const { gaze, blink } = useLivelyEyes(order);

  return (
    <svg className="card-character" viewBox="0 0 200 205" aria-hidden="true">
      <Body shape={shape} fill={body} />
      <path
        fill={face}
        d="M39 113c1-34 25-52 61-52s60 18 61 52c1 29-15 41-61 41s-62-12-61-41Z"
      />
      <Eye x="73" blink={blink} gaze={gaze} />
      <Eye x="127" blink={blink} gaze={gaze} />
    </svg>
  );
}

function Character({ character }) {
  const { gaze, blink } = useLivelyEyes(character.order);

  return (
    <div
      className="stage-character"
      style={{
        "--p": `${character.progress}%`,
        "--rotation": `${character.rotate}deg`,
        "--delay": `${character.delay}ms`,
      }}
    >
      <svg viewBox="0 0 200 205" aria-hidden="true">
        <Body shape={character.shape} fill={character.body} />
        <path
          fill={character.face}
          d="M39 113c1-34 25-52 61-52s60 18 61 52c1 29-15 41-61 41s-62-12-61-41Z"
        />
        <Eye x="73" blink={blink} gaze={gaze} />
        <Eye x="127" blink={blink} gaze={gaze} />
      </svg>
    </div>
  );
}

export default function CharacterStage() {
  return (
    <div className="character-stage" aria-hidden="true">
      {lanes.map((lane, laneIndex) => (
        <span
          className={[
            "character-lane",
            `character-lane--${lane.direction}`,
            `character-lane--origin-${lane.origin}`,
            lane.flush ? `character-lane--flush-${lane.flush}` : "",
            `character-lane--${laneIndex + 1}`,
          ]
            .filter(Boolean)
            .join(" ")}
          key={`${lane.direction}-${lane.x}-${lane.y}`}
          style={{
            "--lane-x": `${lane.x}%`,
            "--lane-y": `${lane.y}%`,
            "--lane-length": `${lane.length}%`,
            "--lane-color": lane.color,
            "--lane-delay": `${lane.delay}ms`,
          }}
        >
          <span className="character-lane-fill" />
          {lane.characters.map((character) => (
            <Character character={character} key={character.order} />
          ))}
        </span>
      ))}
    </div>
  );
}
